import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider } from "react-router-dom";
import { ThemeProvider } from "@/hooks/use-theme";
import { TooltipProvider } from "@/components/ui/tooltip";
import { router } from "@/router";
import { useAuthStore } from "@/stores/auth-store";
import { LiveUpdatesProvider } from "@/providers/LiveUpdatesProvider";
import { SplashScreen } from "@/components/splash-screen";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

function App(): React.ReactNode {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const [showSplash, setShowSplash] = useState(() => {
    // Show splash once per session (resets on login/refresh)
    if (sessionStorage.getItem('spectree-splash-shown')) return false;
    return true;
  });

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  const handleSplashComplete = () => {
    sessionStorage.setItem('spectree-splash-shown', '1');
    setShowSplash(false);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <LiveUpdatesProvider>
        <ThemeProvider defaultTheme="dark" storageKey="spectree-ui-theme">
          <TooltipProvider>
            {showSplash ? (
              <SplashScreen onComplete={handleSplashComplete} />
            ) : (
              <RouterProvider router={router} />
            )}
          </TooltipProvider>
        </ThemeProvider>
      </LiveUpdatesProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
